import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, HelpCircle, X } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { Avatar, HelpTip } from '@/components/ui';
import { Logo } from '@/components/Logo';
import {
  SearchIcon,
  LogoutIcon,
  ClientesIcon,
  ComercialIcon,
  OsIcon,
} from '@/components/icons';
import { ThemeToggle } from '@/components/ThemeToggle';
import { NotificationsBell } from './NotificationsBell';
import { useBuscaGlobal, type BuscaResultado, type BuscaTipo } from '@/lib/busca-global-store';
// S-1/S-3 (BUSCA-UNICA): o catálogo de Configurações é a fonte única dos itens
// de config na busca — sinônimos (buscarItens) e permissão (filtrarPorPermissao
// + useMeusAcessos) são os MESMOS motores da tela de Configurações.
import { buscarItens, filtrarPorPermissao } from '@/pages/admin/configuracoes/catalogo';
import { useMeusAcessos } from '@/lib/acesso-store';

/** Rótulo + ícone por tipo de resultado (nunca mostra id/slug cru). */
const TIPO_META: Record<BuscaTipo, { label: string; Icon: ComponentType<{ className?: string }> }> = {
  config: { label: 'Configurações', Icon: Settings },
  cliente: { label: 'Clientes', Icon: ClientesIcon },
  // O grupo é SEPARADO do de Clientes de propósito. Lead e cliente são
  // coisas diferentes, e a separação é parte da defesa da fase 7: cadastro
  // de lead NUNCA vai para o Omie. Misturar os dois numa lista só faria
  // alguém tratar lead como cliente — e o rótulo de cada item repete a
  // palavra "Lead" por isso mesmo, não só o cabeçalho do grupo.
  lead: { label: 'Leads', Icon: ComercialIcon },
  card: { label: 'Propostas', Icon: ComercialIcon },
  os: { label: 'Ordens de serviço', Icon: OsIcon },
  // BUSCA-UNICA (09/08): a Ajuda deixou de ter barra propria. Um lugar so para
  // procurar — dois campos de busca fazem a pessoa escolher onde tentar, e
  // quem escolhe errado conclui que o sistema nao tem a resposta.
  ajuda: { label: 'Ajuda', Icon: HelpCircle },
};

/**
 * S-2: Configurações PRIMEIRO, com posição travada. O catálogo é estático no
 * cliente (aparece na hora); os dados chegam do servidor com debounce. Se as
 * seções não tivessem ordem fixa, a lista pularia quando o servidor responde
 * e a pessoa clicaria no item errado — por isso os dados entram sempre ABAIXO.
 */
const ORDEM_TIPOS: BuscaTipo[] = ['config', 'cliente', 'lead', 'card', 'os', 'ajuda'];

/**
 * TopBar — barra superior do app.
 *
 * Em desktop: busca global central + notificações + avatar + sair (logo já
 * está na Sidebar). Em mobile: logo + busca compacta + avatar.
 *
 * P2 (AJUSTES-POS-2): a busca virou busca REAL. Digite nome de cliente,
 * produto/observação de proposta, número (#1005) ou CNPJ → dropdown com os
 * resultados agrupados, navega ao clicar. Enter abre o 1º resultado (ou o
 * item em foco pelas setas).
 *
 * BUSCA-UNICA (FILA-05AGO): a barra do topo é A busca do sistema — as telas
 * de Configurações entram como seção própria (S-1), no topo e com posição
 * travada (S-2), respeitando permissão (S-3); Ctrl+K abre de qualquer tela
 * (S-5). A barra local da Configurações saiu (S-4).
 */
export function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [debounced, setDebounced] = useState('');
  const [aberto, setAberto] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  /**
   * 🔴 A BARRA DE PESQUISA QUE CORTAVA TUDO (Frente 2, 12/09/2026).
   *
   * > **Matheus:** *"O app no celular está com uma UI muito ruim, a barra de
   * > pesquisa está cortando as informações."*
   *
   * ⚠️ **MEDIDO em 375px, no beta, antes de mexer:** o input tinha **118px de
   * largura** e **116px de padding** (36 à esquerda pela lupa, 80 à direita).
   * **Sobravam DOIS PIXELS para o texto** — e o placeholder precisa de 322.
   * Não cortava "um pouco": não cabia nada, nem o que a pessoa digitava.
   *
   * 🔴 **Duas causas somadas**, e as duas consertadas aqui:
   *   1. o `pr-20` reservava 80px para o atalho `Ctrl K`, que é
   *      `hidden md:inline-flex` — **não existe no celular**;
   *   2. mesmo sem isso, o campo dividia 375px com logo, tema, sino, avatar e
   *      sair. **Não havia largura para ele naquela linha.**
   *
   * ⚠️ Por isso a cura não é folga de padding: no celular a busca **recolhe
   * numa lupa** e, ao tocar, **toma a linha inteira**. É o padrão que todo app
   * de telefone usa, e o único em que o campo tem largura de verdade.
   * No desktop **nada muda** — este estado só existe abaixo de `md`.
   */
  const [buscaNoCelular, setBuscaNoCelular] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // S-5: quem tinha o foco antes do Ctrl+K — Esc devolve pra lá.
  const focoAnteriorRef = useRef<HTMLElement | null>(null);

  // Debounce ~250ms — só dispara a busca quando o usuário para de digitar.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchValue.trim()), 250);
    return () => clearTimeout(t);
  }, [searchValue]);

  const { data, isFetching } = useBuscaGlobal(debounced);
  const resultados = useMemo<BuscaResultado[]>(() => data?.resultados ?? [], [data]);

  // S-1/S-3: itens de Configurações direto do catálogo (fonte única), com os
  // sinônimos ("imposto" acha alíquota, sem acento) e o filtro de permissão
  // que a tela de Configurações já usa. SEM debounce: catálogo é estático,
  // aparece na hora — e como a seção fica no topo com posição travada, o
  // resultado do servidor entrando depois não move nada que já está na tela.
  const { itens: meusAcessos } = useMeusAcessos();
  const chaves = useMemo(
    () => new Set(meusAcessos.filter((a) => a.permitido).map((a) => a.chave)),
    [meusAcessos],
  );
  const termoInstantaneo = searchValue.trim();
  const configResultados = useMemo<BuscaResultado[]>(() => {
    if (termoInstantaneo.length < 2) return [];
    return filtrarPorPermissao(buscarItens(termoInstantaneo), {
      isAdmin: !!user?.isAdmin,
      chaves,
    })
      .slice(0, 6)
      .map((i) => ({
        tipo: 'config' as const,
        id: `${i.secaoId}-${i.id}`,
        titulo: i.nome,
        subtitulo: `Configurações · ${i.secaoNome}`,
        href: i.para,
      }));
  }, [termoInstantaneo, user?.isAdmin, chaves]);

  // Agrupa mantendo a ordem canônica TRAVADA (config → clientes → propostas → OS).
  const grupos = useMemo(
    () =>
      ORDEM_TIPOS.map((tipo) => ({
        tipo,
        itens:
          tipo === 'config' ? configResultados : resultados.filter((r) => r.tipo === tipo),
      })).filter((g) => g.itens.length > 0),
    [resultados, configResultados],
  );

  // Lista "plana" pra navegação por teclado (setas + Enter).
  const plano = useMemo(() => grupos.flatMap((g) => g.itens), [grupos]);

  const temTermo = termoInstantaneo.length >= 2;
  const mostrarDropdown = aberto && temTermo;
  // S-2: o "Buscando…" é DA SEÇÃO DE DADOS — aparece abaixo das Configurações
  // enquanto o servidor não respondeu, nunca no lugar delas.
  const dadosCarregando = temTermo && (isFetching || debounced !== termoInstantaneo);
  const carregando = dadosCarregando && resultados.length === 0;
  const vazio = temTermo && !carregando && plano.length === 0;

  // Reseta o item ativo sempre que a lista muda.
  useEffect(() => {
    setActiveIndex(-1);
  }, [debounced, resultados]);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!mostrarDropdown) return;
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setAberto(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [mostrarDropdown]);

  // S-5: Ctrl+K (Cmd+K no Mac) foca a busca de qualquer tela. preventDefault é
  // obrigatório — no Chrome, Ctrl+K foca a barra de endereço e o atalho
  // "parece não funcionar". Não dispara quando a pessoa digita em OUTRO campo
  // (o próprio campo de busca é a exceção). Censo de colisões: Ctrl+\ recolhe
  // a sidebar e Ctrl+Z/Ctrl+Shift+Z é o undo do dashboard — Ctrl+K está livre.
  useEffect(() => {
    function aoTeclar(e: globalThis.KeyboardEvent) {
      if (e.key.toLowerCase() !== 'k' || !(e.ctrlKey || e.metaKey)) return;
      const alvo = e.target as HTMLElement | null;
      const tag = alvo?.tagName?.toLowerCase();
      const digitandoEmOutroCampo =
        alvo !== inputRef.current &&
        (tag === 'input' || tag === 'textarea' || tag === 'select' || alvo?.isContentEditable);
      if (digitandoEmOutroCampo) return;
      e.preventDefault();
      focoAnteriorRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // ⚠️ No celular o campo está recolhido: focar sem expandir não faria
      // nada visível — o atalho "não funcionaria" com teclado acoplado.
      setBuscaNoCelular(true);
      setAberto(true);
      // 🔴 Os DOIS focos são necessários, e não é redundância: o campo está
      // escondido por CLASSE (nunca desmontado), e o foco num elemento dentro
      // de `display:none` não pega. O primeiro resolve o desktop, onde ele já
      // está visível; o segundo, o celular, depois de a classe cair.
      inputRef.current?.focus();
      requestAnimationFrame(() => inputRef.current?.focus());
    }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, []);

  function irPara(r: BuscaResultado) {
    setSearchValue('');
    setDebounced('');
    setAberto(false);
    setActiveIndex(-1);
    setBuscaNoCelular(false);
    navigate(r.href);
  }

  /** Abre a busca no celular e põe o cursor nela — um toque, não dois. */
  function abrirBuscaNoCelular() {
    setBuscaNoCelular(true);
    setAberto(true);
    // O campo só deixa de estar escondido no render seguinte — ver o
    // comentário dos dois focos no atalho Ctrl+K.
    inputRef.current?.focus();
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function fecharBuscaNoCelular() {
    setBuscaNoCelular(false);
    setAberto(false);
    setSearchValue('');
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (plano.length === 0) return;
    // Enter → item em foco (setas) ou o 1º resultado.
    const alvo = activeIndex >= 0 ? plano[activeIndex] : plano[0];
    if (alvo) irPara(alvo);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setAberto(false);
      // ⚠️ No celular, Esc também RECOLHE — senão o campo fica aberto com o
      // dropdown fechado, ocupando a linha e escondendo as outras ações.
      setBuscaNoCelular(false);
      // S-5: Esc devolve o foco pra onde a pessoa estava antes do Ctrl+K.
      const anterior = focoAnteriorRef.current;
      focoAnteriorRef.current = null;
      if (anterior && document.contains(anterior)) anterior.focus();
      else inputRef.current?.blur();
      return;
    }
    if (!mostrarDropdown || plano.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % plano.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? plano.length - 1 : i - 1));
    }
  }

  return (
    <header className="sticky top-0 z-20 bg-paper/80 dark:bg-paper-dark/80 backdrop-blur-md border-b border-line-soft dark:border-line-soft-dark">
      <div className="flex items-center h-14 px-4 md:px-6 gap-3">
        {/* Logo só em mobile (no desktop fica na Sidebar). Some quando a busca
            toma a linha — é a largura dela que estava faltando. */}
        <div className={`md:hidden flex-shrink-0 ${buscaNoCelular ? 'hidden' : ''}`}>
          <Logo size="sm" />
        </div>

        {/* 🔴 A lupa do celular: 44px de alvo, e um toque só até o cursor.
            Não existe no desktop, onde o campo sempre coube. */}
        {!buscaNoCelular && (
          <button
            type="button"
            onClick={abrirBuscaNoCelular}
            aria-label="Buscar"
            aria-expanded={false}
            data-testid="topbar-search-abrir"
            className="md:hidden flex items-center justify-center w-11 h-11 rounded-lg text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors"
          >
            <SearchIcon className="w-5 h-5" />
          </button>
        )}

        {/* Busca global — toma o espaço central. min-w-0: sem ele o tamanho
            intrínseco do input estourava a linha em 375px (17px de barra
            horizontal medidos no S-6).
            ⚠️ No celular ela fica ESCONDIDA até a lupa ser tocada: inline, o
            campo tinha 2px úteis de texto (ver `buscaNoCelular`). */}
        <div
          ref={wrapperRef}
          className={`relative flex-1 min-w-0 max-w-xl mx-auto md:mx-0 ${
            buscaNoCelular ? '' : 'hidden md:block'
          }`}
        >
          <form onSubmit={handleSubmit} role="search" aria-label="Busca global">
            <div className="relative flex items-center">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setAberto(true)}
                onKeyDown={handleKeyDown}
                placeholder="Buscar cliente, proposta, OS ou configuração"
                data-testid="topbar-search-input"
                role="combobox"
                aria-expanded={mostrarDropdown}
                aria-controls="topbar-search-results"
                aria-autocomplete="list"
                aria-activedescendant={activeIndex >= 0 ? `busca-opcao-${activeIndex}` : undefined}
                autoComplete="off"
                className="w-full h-10 md:h-9 pl-9 pr-10 md:pr-20 rounded-lg bg-paper-warm dark:bg-paper-dark-surface border border-line-soft dark:border-line-soft-dark text-sm text-ink-1 dark:text-ink-dark-1 placeholder-ink-3 focus:outline-none focus:border-red focus:ring-2 focus:ring-red-soft dark:focus:ring-red-soft-dark transition-colors"
              />
              {/* S-5: atalho que ninguém descobre não existe — a dica fica na barra. */}
              <kbd
                aria-hidden="true"
                className="hidden md:inline-flex absolute right-9 top-1/2 -translate-y-1/2 items-center gap-0.5 rounded border border-line dark:border-line-dark px-1.5 py-0.5 text-[10px] font-semibold text-ink-3 dark:text-ink-dark-3"
              >
                Ctrl K
              </kbd>
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <HelpTip
                  lado="baixo"
                  texto="Busca cliente pelo nome ou CNPJ, proposta pelo número (#1005), OS pelo número ou título, e qualquer tela de Configurações pelo nome (ex.: imposto, feriado). Ctrl+K abre a busca de qualquer tela."
                />
              </span>
            </div>
          </form>

          {/* Dropdown de resultados */}
          {mostrarDropdown && (
            <div
              id="topbar-search-results"
              role="listbox"
              aria-label="Resultados da busca"
              data-testid="topbar-search-results"
              className="absolute left-0 right-0 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-line-soft dark:border-line-soft-dark bg-paper dark:bg-paper-dark shadow-lg z-30"
            >
              {/* S-2: as seções renderizam na ordem TRAVADA (config no topo);
                  o "Buscando…" dos dados aparece ABAIXO do que já está na
                  tela — nada que a pessoa vê muda de posição quando o
                  servidor responde. */}
              {grupos.map((grupo) => {
                const meta = TIPO_META[grupo.tipo];
                return (
                  <div key={grupo.tipo} className="py-1">
                    <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                      {meta.label}
                    </div>
                    {grupo.itens.map((r) => {
                      const idx = plano.indexOf(r);
                      const ativo = idx === activeIndex;
                      return (
                        <button
                          key={`${r.tipo}-${r.id}`}
                          id={`busca-opcao-${idx}`}
                          type="button"
                          role="option"
                          aria-selected={ativo}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => irPara(r)}
                          className={`w-full flex items-start gap-3 px-3 py-2 text-left transition-colors ${
                            ativo
                              ? 'bg-paper-warm dark:bg-paper-dark-warm'
                              : 'hover:bg-paper-warm dark:hover:bg-paper-dark-warm'
                          }`}
                        >
                          <meta.Icon className="w-4 h-4 mt-0.5 flex-shrink-0 text-ink-3 dark:text-ink-dark-3" />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm text-ink-1 dark:text-ink-dark-1 truncate">
                              {r.titulo}
                            </span>
                            {r.subtitulo && (
                              <span className="block text-xs text-ink-3 dark:text-ink-dark-3 truncate">
                                {r.subtitulo}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}

              {carregando && (
                <div
                  className="px-4 py-3 text-sm text-ink-3 dark:text-ink-dark-3"
                  data-testid="topbar-search-carregando"
                >
                  Buscando clientes, propostas e OS…
                </div>
              )}

              {vazio && (
                <div
                  className="px-4 py-4 text-sm text-ink-3 dark:text-ink-dark-3"
                  data-testid="topbar-search-empty"
                >
                  Nada com{' '}
                  <span className="font-medium text-ink-2 dark:text-ink-dark-2">
                    “{termoInstantaneo}”
                  </span>
                  . Tente o nome do cliente, o nº da proposta, ou termos como imposto, prazo,
                  lembrete e feriado.
                </div>
              )}

              {/* S-6: leitor de tela anuncia o tamanho do resultado. */}
              <span className="sr-only" role="status" aria-live="polite">
                {plano.length === 1 ? '1 resultado' : `${plano.length} resultados`}
              </span>
            </div>
          )}
        </div>

        {/* 🔴 A saída da busca no celular — 44px, ao lado do campo. Sem ela, a
            única forma de voltar seria recarregar a tela. */}
        {buscaNoCelular && (
          <button
            type="button"
            onClick={fecharBuscaNoCelular}
            aria-label="Fechar busca"
            data-testid="topbar-search-fechar"
            className="md:hidden flex items-center justify-center w-11 h-11 flex-shrink-0 rounded-lg text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Ações à direita — saem de cena enquanto a busca ocupa a linha. */}
        <div
          className={`items-center gap-1 flex-shrink-0 ml-auto ${
            buscaNoCelular ? 'hidden md:flex' : 'flex'
          }`}
        >
          <ThemeToggle />
          <NotificationsBell userId={user?.id ?? 'anonymous'} />

          {/* Avatar — clicável, leva pra /perfil */}
          <Link
            to="/perfil"
            // Alvo de toque: 40px medidos no mobile (o avatar `sm` + py-1),
            // contra o piso de 44 do sistema. `min-h` em vez de padding: o
            // padding empurraria a linha inteira da TopBar.
            className="ml-2 flex items-center gap-2 min-h-[44px] sm:min-h-0 rounded-lg px-1 py-1 hover:bg-paper-warm dark:hover:bg-paper-dark-warm transition-colors"
            title="Meu perfil"
            aria-label="Meu perfil"
          >
            <Avatar name={user?.name ?? 'U'} src={user?.fotoUrl} size="sm" />
            <div className="hidden lg:flex flex-col">
              <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 leading-tight">
                {user?.name}
              </span>
              <span className="text-xs text-ink-3 dark:text-ink-dark-3 leading-tight">
                {user?.isAdmin ? 'Administrador' : 'Colaborador'}
              </span>
            </div>
          </Link>
          <button
            type="button"
            onClick={logout}
            aria-label="Sair"
            title="Sair"
            data-testid="topbar-logout-button"
            className="ml-1 p-3 md:p-2 rounded-lg text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm hover:text-red transition-colors"
          >
            <LogoutIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
